import { firstNameOf } from './participantTitle'

/**
 * How a user is named, in one place, for both sides of the stack.
 *
 * Two functions rather than one with a flag, because the choice is per call site
 * and needs to be visible there: a nickname that applies in eleven services and
 * deliberately not in the twelfth is exactly the kind of thing an implicit default
 * gets wrong silently. The client resolves its own viewer by the same rule so a
 * name it renders before the server answers never differs from the one that comes
 * back.
 */

/** Fields any naming function may read. A server `User` row and a client `IUser` both fit. */
export interface INameableUser {
  nickname?: string | null
  firstName?: string | null
  lastName?: string | null
  email?: string | null
}

/**
 * The name to show a human, nickname first. The default for in-app surfaces.
 *
 * ⚠️ Reads `nickname`, so a server caller narrowing columns with `select` MUST
 * include it — use `USER_NAME_COLUMNS` (server/src/common/userDisplay.ts) rather
 * than an inline list. A `select` that omits it yields `undefined` and this quietly
 * returns the legal name instead: no crash and no type error, because TypeORM's
 * narrowed `find` still returns the full entity type.
 */
export const displayName = (user: INameableUser): string => user.nickname?.trim() || legalName(user)

/**
 * The name on the account, never the nickname.
 *
 * For the places a nickname would be wrong rather than merely unhelpful:
 * external documents, the client-facing contact card, and text that gets
 * embedded or frozen into a stored row.
 */
export const legalName = (user: Omit<INameableUser, 'nickname'>): string =>
  `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || (user.email ?? '')

/**
 * The compact form of a name, for surfaces that show one name per row: a message
 * author, a typing line, a DM title, a reaction tooltip.
 *
 * A nickname is returned WHOLE, not cut at the first space. That is the whole
 * point of the field — someone who sets "Big Mike" chose that as their short
 * name, and `firstNameOf` reducing it to "Big" makes the nickname worse than not
 * having one. Only a legal name gets abbreviated, because "Michael Abadie" was
 * never a chosen short form.
 *
 * Resolved once and carried in payloads: a bare string handed to `firstNameOf`
 * cannot tell a nickname from a legal name, so it truncates both.
 */
export const shortDisplayName = (user: INameableUser): string =>
  user.nickname?.trim() || firstNameOf(legalName(user))
